import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, FileDown, FileText, Forward, Gavel, HelpCircle, Inbox, Loader2, Mail, Plus, Reply, RotateCcw, Search, Trash2, Undo2, UserPlus, Users, X } from "lucide-react";
import { SecurityCheck } from "@/components/submissions/SecurityCheck";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { FileChips, Messages, StatePill, StatusPill, Timeline, daysLeft, statusOf } from "@/components/submissions/shared";

const field = "w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]";
const label = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#57534E]";
const ACTIONS = [["in_progress", "In Progress"], ["replied", "Replied"], ["rejected", "Rejected"], ["declined", "Declined"], ["closed", "Close"]];
const NEEDS_NOTE = ["in_progress", "replied"];
const APPEALING = ["appeal_submitted", "appeal_review"];

const SaveButtons = ({ busy, disabled, onSave, testId }) => (
  <button disabled={busy || disabled} onClick={() => onSave()} className="mt-3 flex w-full items-center justify-center gap-1.5 bg-[#0A0A0A] py-2.5 text-xs font-bold text-white hover:bg-[#E62314] disabled:opacity-40" data-testid={`${testId}-save`}>{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Save</button>
);

const useAction = (sid, onSaved) => {
  const [busy, setBusy] = useState(false);
  const run = async (path, body, ok) => {
    setBusy(true);
    try { const r = await api.post(`/queues/items/${sid}/${path}`, body); onSaved(r.data); toast.success(ok); return true; }
    catch (e) { toast.error(errText(e)); return false; } finally { setBusy(false); }
  };
  return [busy, run];
};

const ReopenCard = ({ s, onSaved }) => {
  const [note, setNote] = useState("");
  const [busy, run] = useAction(s.id, onSaved);
  return (
    <section className="border-2 border-[#22C55E] bg-white p-5" data-testid="reopen-card">
      <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><RotateCcw className="h-4 w-4" />Reopen submission</h4>
      <p className="mt-1 text-xs text-[#57534E]">Owner only. Use this if the outcome was a mistake. It goes back to Active and their status page starts a second look.</p>
      <textarea rows={3} value={note} maxLength={3000} onChange={(e) => setNote(e.target.value)} placeholder="Note for them (optional)" className={`${field} mt-3`} data-testid="reopen-note" />
      <button disabled={busy} onClick={async () => { if (await run("reopen", { note: note.trim() }, "Submission reopened")) setNote(""); }} className="mt-3 flex w-full items-center justify-center gap-1.5 bg-[#0A0A0A] py-2.5 text-xs font-bold text-white hover:bg-[#22C55E] hover:text-black disabled:opacity-40" data-testid="reopen-submit">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}Reopen</button>
    </section>
  );
};

const RecordsCard = ({ s, isAdmin, onSent, onDeleted }) => {
  const [busy, setBusy] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [note, setNote] = useState("");
  const send = async (kind) => {
    setBusy(kind);
    try {
      await api.post(`/queues/items/${s.id}/data-request`, { kind, reason: note.trim() });
      if (!isAdmin) { toast.success("Request sent to the owner. You'll get an email once it's decided"); setNote(""); }
      else if (kind === "delete") { toast.success("Submission deleted and the person has been emailed"); onDeleted(); }
      else { toast.success("A copy has been emailed to them"); onSent(); }
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); setConfirmDelete(false); }
  };
  const pdf = async () => {
    setBusy("pdf");
    try {
      const r = await api.get(`/queues/items/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank", "noopener"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const btn = "flex w-full items-center justify-center gap-1.5 py-2.5 text-xs font-bold disabled:opacity-40";
  const spin = (k, Icon) => (busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />);
  return (
    <section className="border border-black/15 bg-white p-5" data-testid="records-card">
      <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><FileText className="h-4 w-4" />Copies &amp; deletion</h4>
      <p className="mt-1 text-xs text-[#57534E]">{isAdmin ? "These happen straight away." : "These go to the owner to approve or decline."}</p>
      <div className="mt-3">
        {s.can_pdf
          ? <button disabled={!!busy} onClick={pdf} className={`${btn} bg-[#0A0A0A] text-white hover:bg-[#E62314]`} data-testid="records-pdf-download">{spin("pdf", FileDown)}Download PDF copy</button>
          : <button disabled={!!busy} onClick={() => send("pdf")} className={`${btn} border border-black/80 hover:bg-black hover:text-white`} data-testid="records-pdf-request">{spin("pdf", FileDown)}Request PDF copy</button>}
        <p className="mt-1 text-xs text-[#57534E]">For your own records, for example legal matters. Not sent to the person.</p>
      </div>
      <div className="mt-4 border-t border-black/10 pt-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#57534E]">Requests from the person</p>
        <div className="mt-2 space-y-2">
            {!isAdmin && <textarea rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} placeholder="Note for the owner (optional)" className={field} data-testid="records-note" />}
            <button disabled={!!busy} onClick={() => send("copy")} className={`${btn} bg-[#0A0A0A] text-white hover:bg-[#E62314]`} data-testid="records-copy">{spin("copy", Mail)}{isAdmin ? "Email them a copy" : "Request a copy for them"}</button>
            {confirmDelete ? (
              <div className="border border-[#E62314] p-3" data-testid="records-delete-confirm">
                <p className="text-xs font-semibold">{isAdmin ? "This permanently deletes the submission and emails them to confirm. It can't be undone." : "Send a deletion request to the owner?"}</p>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  <button onClick={() => setConfirmDelete(false)} className={`${btn} border border-black/15 hover:border-black`} data-testid="records-delete-cancel">Cancel</button>
                  <button disabled={!!busy} onClick={() => send("delete")} className={`${btn} bg-[#E62314] text-white hover:bg-[#0A0A0A]`} data-testid="records-delete-yes">{spin("delete", Trash2)}{isAdmin ? "Delete" : "Send"}</button>
                </div>
              </div>
            ) : <button onClick={() => setConfirmDelete(true)} className={`${btn} border border-[#E62314] text-[#C21B0E] hover:bg-[#E62314] hover:text-white`} data-testid="records-delete"><Trash2 className="h-3.5 w-3.5" />{isAdmin ? "Delete their submission" : "Request deletion"}</button>}
        </div>
      </div>
    </section>
  );
};

const NotifyCard = ({ s, onSaved }) => {
  const [busy, run] = useAction(s.id, onSaved);
  return (
    <section className="border border-black/15 bg-white p-5" data-testid="notify-card">
      <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><Mail className="h-4 w-4" />Let them know</h4>
      <p className="mt-1 text-xs text-[#57534E]">Sends one short email saying there's an update on their submission, with a button to their status page. No details are included.</p>
      <button disabled={busy} onClick={() => run("notify", {}, "Update email sent")} className="mt-3 flex w-full items-center justify-center gap-1.5 bg-[#E62314] py-2.5 text-xs font-bold text-white hover:bg-[#0A0A0A] disabled:opacity-40" data-testid="notify-send">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}Email them about an update</button>
      {s.last_notified_at && <p className="mt-2 text-xs text-[#57534E]" data-testid="notify-last">Last emailed {fmtDateTime(s.last_notified_at)}</p>}
    </section>
  );
};

const StatusCard = ({ s, onSaved }) => {
  const [status, setStatus] = useState("in_progress");
  const [note, setNote] = useState("");
  const [questions, setQuestions] = useState(["Why should we reconsider your submission?", "Is there anything new we should know?"]);
  const [allowUpload, setAllowUpload] = useState(true);
  const secondLook = (s.history || []).some((e) => e.status === "reopened");
  const [allowAppeal, setAllowAppeal] = useState(!secondLook);
  const [allowReply, setAllowReply] = useState(true);
  const [busy, run] = useAction(s.id, onSaved);
  const save = async () => {
    const body = { status, note: note.trim(), questions: questions.filter((q) => q.trim()), allow_upload: allowUpload, allow_appeal: allowAppeal, allow_reply: allowReply };
    if (await run("status", body, `${ACTIONS.find(([k]) => k === status)?.[1] || "Update"} saved`)) setNote("");
  };
  return (
    <section className="border border-black/15 bg-white p-5" data-testid="status-card">
      <h4 className="font-display text-lg font-extrabold">Update status</h4>
      <p className="mt-1 text-xs text-[#57534E]">You can add as many In Progress updates as you need, each with its own note.</p>
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        {ACTIONS.map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} className={`px-3 py-2 text-xs font-bold ${status === k ? "bg-[#0A0A0A] text-white" : "border border-black/15 hover:border-black"}`} data-testid={`status-choice-${k}`}>{l}</button>
        ))}
      </div>
      <label className="mt-4 block"><span className={label}>{status === "replied" ? "Your reply (required)" : status === "in_progress" ? "Note for them (required)" : "Note for them (optional)"}</span>
        <textarea rows={3} value={note} maxLength={3000} onChange={(e) => setNote(e.target.value)} placeholder={status === "replied" ? "Write your reply. They can reply back from their status page" : "e.g. Callum has passed your submission to his talent manager"} className={field} data-testid="status-note" /></label>
      {status === "replied" && <label className="mt-3 flex items-center justify-between gap-3 text-sm font-semibold">Let them reply<Switch checked={allowReply} onCheckedChange={setAllowReply} data-testid="status-allow-reply" /></label>}
      {status === "declined" && (
        <div className="mt-4 border border-dashed border-black/25 bg-[#FBF9F5] p-3" data-testid="appeal-setup">
          <label className="flex items-center justify-between gap-3 text-sm font-semibold">Let them appeal (30 days)<Switch checked={allowAppeal} onCheckedChange={setAllowAppeal} data-testid="appeal-allow" /></label>
          <p className="mt-1 text-xs text-[#57534E]">{allowAppeal ? "They can ask you to take a second look and reopen it." : "No appeal button will show on their status page."}</p>
          {allowAppeal && <>
          <ul className="mt-2 space-y-2">
            {questions.map((q, i) => (
              <li key={i} className="flex gap-1.5">
                <input value={q} maxLength={500} onChange={(e) => setQuestions((x) => x.map((v, j) => (j === i ? e.target.value : v)))} placeholder={`Question ${i + 1}`} className={field} data-testid={`appeal-question-${i}`} />
                <button onClick={() => setQuestions((x) => x.filter((_, j) => j !== i))} aria-label="Remove question" className="shrink-0 px-2 hover:text-[#E62314]" data-testid={`appeal-question-${i}-remove`}><X className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
          {questions.length < 10 && <button onClick={() => setQuestions((x) => [...x, ""])} className="mt-2 flex items-center gap-1 text-xs font-bold hover:text-[#E62314]" data-testid="appeal-question-add"><Plus className="h-3.5 w-3.5" />Add question</button>}
          <label className="mt-3 flex items-center justify-between gap-3 text-sm font-semibold">Allow file upload<Switch checked={allowUpload} onCheckedChange={setAllowUpload} data-testid="appeal-allow-upload" /></label>
          </>}
        </div>
      )}
      <SaveButtons busy={busy} disabled={NEEDS_NOTE.includes(status) && !note.trim()} onSave={save} testId="status" />
    </section>
  );
};

const MessageCard = ({ s, onSaved, path, title, Icon, placeholder, ok, testId, replyTo, onClearReply }) => {
  const [msg, setMsg] = useState("");
  const [allow, setAllow] = useState(true);
  const [busy, run] = useAction(s.id, onSaved);
  const save = async () => {
    if (await run(path, { message: msg.trim(), allow_reply: allow, ...(replyTo ? { reply_to: replyTo.id } : {}) }, ok)) { setMsg(""); if (onClearReply) onClearReply(); }
  };
  return (
    <section id={`${testId}-card`} className="border border-black/15 bg-white p-5" data-testid={`${testId}-card`}>
      <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><Icon className="h-4 w-4" />{title}</h4>
      {replyTo && (
        <div className="mt-3 flex items-start justify-between gap-2 border-l-2 border-black/30 bg-[#F3F2EF] px-3 py-2" data-testid={`${testId}-replying-to`}>
          <p className="line-clamp-2 break-words text-xs italic text-[#57534E]">Replying to: {replyTo.text}</p>
          <button onClick={onClearReply} aria-label="Cancel reply" className="shrink-0 hover:text-[#E62314]" data-testid={`${testId}-replying-clear`}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      <textarea rows={4} value={msg} maxLength={5000} onChange={(e) => setMsg(e.target.value)} placeholder={placeholder} className={`${field} mt-3`} data-testid={`${testId}-message`} />
      <label className="mt-3 flex items-center justify-between gap-3 text-sm font-semibold">Let them reply<Switch checked={allow} onCheckedChange={setAllow} data-testid={`${testId}-allow-reply`} /></label>
      <SaveButtons busy={busy} disabled={!msg.trim()} onSave={save} testId={testId} />
    </section>
  );
};

const AppealCard = ({ s, onSaved }) => {
  const fresh = s.status === "appeal_submitted";
  const [decision, setDecision] = useState(fresh ? "review" : "reopen");
  const [note, setNote] = useState("");
  const [busy, run] = useAction(s.id, onSaved);
  useEffect(() => { setDecision(fresh ? "review" : "reopen"); setNote(""); return undefined; }, [fresh]);
  const options = [...(fresh ? [["review", "Appeal Under Review"]] : []), ["reopen", "Approve · reopen"], ["decline", "Decline appeal"]];
  const done = decision === "review" ? "Marked Appeal Under Review" : "Appeal decided";
  return (
    <section className="border-2 border-[#8B5CF6] bg-white p-5" data-testid="appeal-card">
      <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><Gavel className="h-4 w-4" />Appeal decision</h4>
      <p className="mt-3 text-xs text-[#57534E]">{fresh ? "New appeal. Mark it as Appeal Under Review while you look at it, or decide it now. " : ""}Approving reopens their submission automatically. Declining is final.</p>
      <div className={`mt-3 grid gap-1.5 ${fresh ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2"}`}>
        {options.map(([k, l]) => (
          <button key={k} onClick={() => setDecision(k)} className={`px-3 py-2 text-xs font-bold ${decision === k ? "bg-[#0A0A0A] text-white" : "border border-black/15 hover:border-black"}`} data-testid={`appeal-decision-${k}`}>{l}</button>
        ))}
      </div>
      <textarea rows={3} value={note} maxLength={3000} onChange={(e) => setNote(e.target.value)} placeholder="Note for them (optional)" className={`${field} mt-3`} data-testid="appeal-decision-note" />
      <SaveButtons busy={busy} onSave={() => run(`appeal/${decision}`, { note: note.trim() }, done)} testId="appeal-decision" />
    </section>
  );
};

const FindCard = ({ s, onOpen }) => {
  const ref = (s.message.match(/CM-[A-Z0-9]{6}/) || [])[0];
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);
  const find = async () => {
    setBusy(true);
    try { setFound((await api.get("/queues/lookup", { params: { ref, request_id: s.id } })).data); }
    catch (e) { toast.error(errText(e)); setFound(null); } finally { setBusy(false); }
  };
  if (!ref) return null;
  return (
    <section className="border-2 border-[#E62314] bg-white p-5" data-testid="find-card">
      <p className={label}>Their submission</p>
      <p className="font-mono text-sm tracking-widest">{ref}</p>
      {!found ? (
        <button onClick={find} disabled={busy} className="mt-3 flex items-center gap-1.5 bg-[#0A0A0A] px-4 py-2.5 text-xs font-bold text-white hover:bg-[#E62314] disabled:opacity-40" data-testid="find-submission">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}Find their submission</button>
      ) : (
        <div className="mt-3 space-y-2" data-testid="find-result">
          <p className="text-sm"><span className="font-bold">{found.name}</span> · {found.status_label}{found.closed ? " · Closed" : ""}</p>
          {!found.email_match && <p className="text-xs font-semibold text-[#C21B0E]" data-testid="find-email-mismatch">The email on this request doesn't match the email on that submission.</p>}
          {!found.closed && <p className="text-xs text-[#57534E]" data-testid="find-not-closed">This submission isn't closed yet. Copies and deletions are only available for closed submissions.</p>}
          <button onClick={() => (found.locked ? setCheck(found) : onOpen(found.id))} className="flex items-center gap-1.5 bg-[#0A0A0A] px-4 py-2.5 text-xs font-bold text-white hover:bg-[#E62314]" data-testid="find-open">{found.locked ? "Security verification to open" : "Open their submission"}<ArrowRight className="h-3.5 w-3.5" /></button>
        </div>
      )}
      <SecurityCheck item={check} onClose={() => { setCheck(null); find(); }} onPassed={(fid) => { setCheck(null); onOpen(fid); }} />
    </section>
  );
};

const forwardText = (s, note) => [
  note.trim() && `Note: ${note.trim()}`, "", `Reference: ${s.ref}`, `From: ${s.name} (${s.email})`,
  `Inquiry type: ${s.category}${s.topic ? ` · ${s.topic}` : ""}`, `Sent: ${fmtDateTime(s.created_at)}`, "", "Message:", s.message, "", "History:",
  ...(s.history || []).map((e) => `${fmtDateTime(e.at)} · ${e.status ? statusOf(e.status).label : e.kind}${e.text ? `: ${e.text}` : ""}`),
].filter((l) => l !== false && l !== "").join("\n");

const isPhone = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent || "");
const gmailLink = (to, subject, body) => {
  const ua = navigator.userAgent || "";
  const web = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${body}`;
  if (/iphone|ipad|ipod/i.test(ua)) return `googlegmail:///co?to=${encodeURIComponent(to)}&subject=${encodeURIComponent(subject)}&body=${body}`;
  if (/android/i.test(ua)) return `intent://${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${body}#Intent;scheme=mailto;package=com.google.android.gm;S.browser_fallback_url=${encodeURIComponent(web)};end`;
  return web;
};

const ForwardCard = ({ s }) => {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim());
  const subject = `Forwarded submission · ${s.ref}`;
  const body = encodeURIComponent(forwardText(s, note));
  const send = async () => {
    setBusy(true);
    try { await api.post(`/queues/items/${s.id}/forward`, { to: to.trim(), note: note.trim() }); toast.success(`Submission forwarded to ${to.trim()}`); setTo(""); setNote(""); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const btn = "flex w-full items-center justify-center gap-1.5 py-2.5 text-xs font-bold";
  return (
    <section className="border border-black/15 bg-white p-5" data-testid="forward-card">
      <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><Forward className="h-4 w-4" />Forward by email</h4>
      <p className="mt-1 text-xs text-[#57534E]">Email the full submission to someone, for example your manager. It stays in this inbox. To hand it to a colleague, use Pass to a colleague.</p>
      <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Their email address" className={`${field} mt-3`} data-testid="forward-to" />
      <textarea rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className={`${field} mt-2`} data-testid="forward-note" />
      <div className="mt-3 space-y-1.5">
        <button disabled={!ok || busy} onClick={send} className={`${btn} bg-[#0A0A0A] text-white hover:bg-[#E62314] disabled:opacity-40`} data-testid="forward-send">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}Send from Cresway Media</button>
        <a href={ok ? gmailLink(to.trim(), subject, body) : undefined} target={isPhone() ? undefined : "_blank"} rel="noreferrer" aria-disabled={!ok} className={`${btn} border border-black/80 hover:bg-black hover:text-white ${ok ? "" : "pointer-events-none opacity-40"}`} data-testid="forward-gmail">Open in Gmail</a>
        <a href={ok ? `mailto:${encodeURIComponent(to.trim())}?subject=${encodeURIComponent(subject)}&body=${body}` : undefined} aria-disabled={!ok} className={`${btn} border border-black/80 hover:bg-black hover:text-white ${ok ? "" : "pointer-events-none opacity-40"}`} data-testid="forward-mail-app">Open in my email app</a>
      </div>
    </section>
  );
};

const NotifySwitch = ({ s, checked, onChange, testId }) => (
  <label className="mt-3 flex items-start justify-between gap-3 border border-black/15 bg-[#FBF9F5] p-3 text-sm font-semibold">
    <span>Would you like to send an email to this submitter?<span className="mt-0.5 block text-xs font-normal text-[#57534E]">It tells them who will be looking after their {APPEALING.includes(s.status) ? "appeal" : "submission"}.</span></span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

const AssignCard = ({ s, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/queues/items/${s.id}/assign`, { notify }); toast.success(`Moved to your inbox${notify ? " and they've been emailed" : ""}`); onDone(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <section className="border-2 border-[#0A0A0A] bg-white p-5" data-testid="assign-card">
      <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><Inbox className="h-4 w-4" />Not assigned yet</h4>
      <p className="mt-1 text-xs text-[#57534E]">{s.can_claim ? "Owner only. Assign it to yourself, or use Assign to someone else below." : "You handled this submission before its appeal, so assign it to someone independent below."}</p>
      {s.can_claim && <NotifySwitch s={s} checked={notify} onChange={setNotify} testId="assign-notify" />}
      {s.can_claim && <button disabled={busy} onClick={go} className="mt-3 flex w-full items-center justify-center gap-1.5 bg-[#0A0A0A] py-2.5 text-xs font-bold text-white hover:bg-[#E62314] disabled:opacity-40" data-testid="assign-to-me">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}Assign to me</button>}
    </section>
  );
};

const PassOnCard = ({ s, isAdmin, onDone }) => {
  const [team, setTeam] = useState(null);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmBack, setConfirmBack] = useState(false);
  const [notify, setNotify] = useState(false);
  useEffect(() => {
    api.get("/queues/team").then((r) => setTeam(Array.isArray(r.data) ? r.data : [])).catch(() => setTeam([]));
    return undefined;
  }, []);
  const excluded = s.appeal_excluded || [];
  const options = (team || []).filter((u) => u.id !== s.assigned_to && (u.owner || !excluded.includes(u.id)));
  const where = APPEALING.includes(s.status) ? "Appeals" : "its queue";
  const call = async (path, body, ok) => {
    setBusy(path);
    try { await api.post(`/queues/items/${s.id}/${path}`, body); toast.success(ok); onDone(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const name = options.find((u) => u.id === to)?.name;
  const btn = "flex w-full items-center justify-center gap-1.5 py-2.5 text-xs font-bold disabled:opacity-40";
  const p = s.pending_transfer;
  if (p) {
    return (
      <section className="border-2 border-[#B45309] bg-[#FFF4E5] p-5" data-testid="pass-on-pending">
        <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><Users className="h-4 w-4" />Waiting for {p.to_name} to accept</h4>
        <p className="mt-1 text-xs text-[#57534E]">{p.by_name} offered it {fmtDateTime(p.at)}. It stays {s.assigned_to ? `in ${s.mine ? "your" : `${s.assigned_name}'s`} inbox` : "unassigned"} until {p.to_name} accepts. If they decline, nothing changes.</p>
        {p.note && <p className="mt-2 whitespace-pre-wrap break-words text-xs">Note: {p.note}</p>}
        <button disabled={!!busy} onClick={() => call("transfer/cancel", {}, "Hand-over cancelled")} className={`${btn} mt-3 border border-black/80 hover:bg-black hover:text-white`} data-testid="pass-on-cancel">{busy === "transfer/cancel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}Cancel hand-over</button>
      </section>
    );
  }
  return (
    <section className="border-2 border-[#0A0A0A] bg-white p-5" data-testid="pass-on-card">
      <h4 className="flex items-center gap-2 font-display text-lg font-extrabold"><Users className="h-4 w-4" />{s.assigned_to ? "Pass to a colleague" : "Assign to someone else"}</h4>
      <p className="mt-1 text-xs text-[#57534E]">They get an email and have to accept it first. Once they accept, it moves into their inbox{isAdmin ? "" : " and you won't be able to open it any more"}.</p>
      <select value={to} onChange={(e) => setTo(e.target.value)} className={`${field} mt-3`} data-testid="pass-on-to">
        <option value="">{!team ? "Loading team..." : options.length ? "Choose a colleague" : "No one else can receive it"}</option>
        {options.map((u) => <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` · ${u.job_title}` : ""} ({u.inbox} active)</option>)}
      </select>
      <textarea rows={2} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} placeholder="Note for them (optional, only the team sees it)" className={`${field} mt-2`} data-testid="pass-on-note" />
      <NotifySwitch s={s} checked={notify} onChange={setNotify} testId="pass-on-notify" />
      <button disabled={!to || !!busy} onClick={() => call("transfer", { to, note: note.trim(), notify }, `Sent to ${name}. It moves to their inbox once they accept`)} className={`${btn} mt-3 bg-[#0A0A0A] text-white hover:bg-[#E62314]`} data-testid="pass-on-send">{busy === "transfer" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}{s.assigned_to ? "Pass it on" : "Assign to them"}</button>
      {s.assigned_to && (confirmBack ? (
        <div className="mt-3 border border-black/30 p-3" data-testid="release-confirm">
          <p className="text-xs font-semibold">Take it out of {s.mine ? "your" : `${s.assigned_name}'s`} inbox and put it back in {where} for anyone to pick up?</p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <button onClick={() => setConfirmBack(false)} className={`${btn} border border-black/15 hover:border-black`} data-testid="release-cancel">Cancel</button>
            <button disabled={!!busy} onClick={() => call("release", { note: note.trim() }, `Back in ${where}`)} className={`${btn} bg-[#0A0A0A] text-white hover:bg-[#E62314]`} data-testid="release-yes">{busy === "release" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Yes, put it back</button>
          </div>
        </div>
      ) : <button onClick={() => setConfirmBack(true)} className={`${btn} mt-2 border border-black/80 hover:bg-black hover:text-white`} data-testid="release"><Undo2 className="h-3.5 w-3.5" />Return to {where}</button>)}
    </section>
  );
};

const LOG_TEXT = {
  assigned: (l) => `${l.by_name} assigned it to themselves`,
  transferred: (l) => `${l.by_name} passed it to ${l.to_name}`,
  assigned_other: (l) => `${l.by_name} assigned it to ${l.to_name}`,
  released: (l) => `${l.by_name} put it back for anyone to pick up`,
  offered: (l) => `${l.by_name} offered it to ${l.to_name} · waiting for them to accept`,
  accepted: (l) => `${l.by_name} accepted it`,
  declined: (l) => `${l.by_name} declined it${l.note ? "" : " · no reason given"}`,
  cancelled: (l) => `${l.by_name} cancelled the hand-over to ${l.to_name}`,
  appeal: () => "Appeal submitted · moved to Appeals for someone independent",
};

const HandlingLog = ({ log }) => (
  <section data-testid="handling-log">
    <p className={`${label} mb-3`}>Who's handled it · team only</p>
    {!log.length ? <p className="text-sm text-[#57534E]">Nobody has picked this up yet.</p> : (
      <ul className="space-y-2">{[...log].reverse().map((l) => (
        <li key={l.id} className="border-l-2 border-black/20 pl-3 text-sm" data-testid={`handling-${l.kind}`}>
          <p className="font-semibold">{(LOG_TEXT[l.kind] || (() => l.kind))(l)}</p>
          {l.note && <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-[#3a3836]">Note: {l.note}</p>}
          <p className="text-xs text-[#57534E]">{fmtDateTime(l.at)}</p>
        </li>
      ))}</ul>
    )}
  </section>
);

const DeclineNotice = ({ s, onDismiss }) => {
  const n = s.decline_notice;
  if (!n) return null;
  const dismiss = async () => { try { await api.post(`/queues/items/${s.id}/decline-notice/dismiss`); onDismiss(); } catch (e) { toast.error(errText(e)); } };
  return (
    <section className="flex items-start justify-between gap-4 border-2 border-[#E62314] bg-white p-5" data-testid="decline-notice">
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C21B0E]">{n.by_name} declined your hand-over</p>
        <p className="mt-2 whitespace-pre-wrap break-words text-sm font-semibold" data-testid="decline-notice-reason">{n.reason || "No reason given."}</p>
        <p className="mt-1 text-xs text-[#57534E]">{fmtDateTime(n.at)} · It's stayed where it was. You can pass it to someone else.</p>
      </div>
      <button onClick={dismiss} aria-label="Dismiss" className="shrink-0 hover:text-[#E62314]" data-testid="decline-notice-dismiss"><X className="h-4 w-4" /></button>
    </section>
  );
};

const NewMessages = ({ s, onReply }) => {
  const h = s.history || [];
  const lastTeam = h.map((e) => e.by === "team" && ["reply", "question"].includes(e.kind)).lastIndexOf(true);
  const fresh = h.filter((e, i) => i > lastTeam && ["answer", "follow_up"].includes(e.kind));
  if (!fresh.length) return null;
  return (
    <section className="border-2 border-[#B45309] bg-[#FFF4E5] p-5" data-testid="new-messages">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">{s.name.split(" ")[0]} sent you {fresh.length === 1 ? "a message" : `${fresh.length} messages`}</p>
      <ul className="mt-3 space-y-3">{fresh.map((e) => (
        <li key={e.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <p className="min-w-0 whitespace-pre-wrap break-words text-sm font-semibold">{e.text}</p>
          <button onClick={() => onReply(e)} className="shrink-0 self-start rounded-full bg-[#0A0A0A] px-5 py-2 text-xs font-bold text-white hover:bg-[#E62314] sm:self-auto" data-testid={`new-message-${e.id}-reply`}>Reply</button>
        </li>
      ))}</ul>
    </section>
  );
};

export const SubmissionView = ({ id, onBack, onOpen, backLabel }) => {
  const { isAdmin } = useAdmin();
  const [s, setS] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const save = (d) => setS((p) => ({ ...p, ...d }));
  const load = useCallback(() => api.get(`/queues/items/${id}`).then((r) => setS(r.data)).catch((e) => { toast.error(errText(e)); onBack(); }), [id, onBack]);
  useEffect(() => { load(); return undefined; }, [load]);
  const openFile = async (f) => {
    try {
      const r = await api.get(`/queues/items/${id}/files/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank", "noopener"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errText(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete submission ${s.ref} permanently?`)) return;
    try { await api.delete(`/queues/items/${id}`); toast.success("Submission deleted"); onBack(); } catch (e) { toast.error(errText(e)); }
  };
  if (!s) return <div className="h-64 animate-pulse bg-black/5" />;
  const a = s.appeal;
  const locked = s.closed && !APPEALING.includes(s.status);
  const afterPass = () => load();
  const replyCard = <MessageCard s={s} onSaved={save} path="reply" title="Reply" Icon={Reply} placeholder="Your reply shows in their messages" ok="Reply added" testId="reply" replyTo={replyTo} onClearReply={() => setReplyTo(null)} />;
  return (
    <div data-testid="submission-view">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold hover:text-[#E62314]" data-testid="submission-back"><ArrowLeft className="h-4 w-4" />{backLabel || "Back to queue"}</button>
      <header className="relative mt-4 overflow-hidden bg-[#0A0A0A] p-6 text-white sm:p-8">
        <div className="pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full bg-[#E62314]/25 blur-3xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-xs tracking-[0.3em] text-zinc-400" data-testid="submission-ref">{s.ref}</p>
            <h3 className="mt-2 break-words font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{s.name}</h3>
            <a href={`mailto:${s.email}`} className="mt-1 block break-all text-sm text-zinc-300 hover:text-white">{s.email}</a>
            <p className="mt-3 break-words text-xs text-zinc-400">{s.category}{s.topic ? ` · ${s.topic}` : ""} · Received {fmtDateTime(s.created_at)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatePill closed={s.closed} testId="submission-state" />
            <StatusPill status={s.status} dark testId="submission-status" />
          </div>
        </div>
        {!locked && <p className="relative mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200" data-testid="submission-assignee"><Inbox className="h-3.5 w-3.5" />{s.assigned_to ? (s.mine ? "In your inbox" : `In ${s.assigned_name}'s inbox`) : APPEALING.includes(s.status) ? "Waiting in Appeals · not assigned" : "Waiting in the queue · not assigned"}{s.pending_transfer ? ` · waiting for ${s.pending_transfer.to_name} to accept` : ""}</p>}
        {a?.deadline && <p className="relative mt-5 text-xs text-zinc-300" data-testid="submission-appeal-window">{a.submitted_at ? `Appeal submitted ${fmtDateTime(a.submitted_at)}` : a.open ? `Appeal window open · ${daysLeft(a.deadline)} days left` : "Appeal window closed"}</p>}
      </header>
      <div className="grid gap-6 border border-t-0 border-black/15 bg-[#FBF9F5] p-5 sm:p-8 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-8">
          <DeclineNotice s={s} onDismiss={() => setS((p) => ({ ...p, decline_notice: null }))} />
          <NewMessages s={s} onReply={(e) => { setReplyTo(e); document.getElementById("reply-card")?.scrollIntoView({ behavior: "smooth", block: "center" }); }} />
          <section>
            <p className={label}>Message</p>
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed" data-testid="submission-message">{s.message}</p>
            {s.files?.length > 0 && <FileChips files={s.files} onOpen={openFile} />}
          </section>
          {s.category === "Submission Copy or Deletion Requests" && <FindCard s={s} onOpen={onOpen} />}
          <section>
            <p className={`${label} mb-5`}>Status history</p>
            <Timeline history={s.history} team onOpenFile={openFile} />
          </section>
          <section>
            <p className={`${label} mb-4`}>Message history</p>
            <Messages history={s.history} team onOpenFile={openFile} />
          </section>
          <HandlingLog log={s.assign_log || []} />
          {isAdmin && <button onClick={remove} className="flex items-center gap-1.5 text-xs font-semibold text-[#57534E] hover:text-[#E62314]" data-testid="submission-delete"><Trash2 className="h-3.5 w-3.5" />Delete submission</button>}
        </div>
        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          {!locked && !s.assigned_to && !s.pending_transfer && <AssignCard s={s} onDone={load} />}
          {!locked && <PassOnCard s={s} isAdmin={isAdmin} onDone={afterPass} />}
          {["appeal_submitted", "appeal_review"].includes(s.status) && <AppealCard s={s} onSaved={save} />}
          {isAdmin && (s.closed || ["declined", "rejected", "appeal_declined", "appeal_submitted", "appeal_review"].includes(s.status)) && <ReopenCard s={s} onSaved={save} />}
          {s.closed ? (
            <section className="border border-dashed border-black/30 bg-white p-5" data-testid="closed-notice">
              <h4 className="font-display text-lg font-extrabold">Closed</h4>
              <p className="mt-1 text-sm text-[#57534E]">This submission is closed, so its status can't be changed. Only the owner can reopen it.</p>
            </section>
          ) : <StatusCard s={s} onSaved={save} />}
          {replyCard}
          <MessageCard s={s} onSaved={save} path="question" title="Ask a question" Icon={HelpCircle} placeholder="They can answer this from their status page" ok="Question sent" testId="question" />
          {s.closed && <RecordsCard s={s} isAdmin={isAdmin} onSent={load} onDeleted={onBack} />}
          <NotifyCard s={s} onSaved={save} />
          <ForwardCard s={s} />
        </aside>
      </div>
    </div>
  );
};
